import { useState, useEffect } from 'react';
import { MapPin, Navigation, ArrowLeft, Map } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { RegionSelector } from '@/components/common/RegionSelector';
import { RouteMap } from '@/components/maps';
import { CalculationLogs } from '@/components/winch/CalculationLogs';
import { winchPricingAPI } from '@/api/winchPricing';
import { winchStartPointsAPI } from '@/api/winchStartPoints';
import type { Region, WinchStartPoint, WinchPriceCalculationResult } from '@/types';

/**
 * Enhanced Winch Pricing Calculator with Region-Based Calculation
 * Shows distance breakdown from start point → pickup → destination
 */
export function WinchPricingCalculatorEnhanced() {
  // Service IDs for winch services
  const WINCH_SERVICES = {
    REGULAR: 13, // Regular Winch service ID
    FULL_DOWN: 14, // Full Down Winch service ID
  };

  const [selectedService, setSelectedService] = useState<number>(WINCH_SERVICES.REGULAR);
  const [pickupRegion, setPickupRegion] = useState<Region | null>(null);
  const [destinationRegion, setDestinationRegion] = useState<Region | null>(null);
  const [activeStartPoint, setActiveStartPoint] = useState<WinchStartPoint | null>(null);
  const [calculation, setCalculation] = useState<WinchPriceCalculationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load active start point
  useEffect(() => {
    loadActiveStartPoint();
  }, []);

  // Calculate price when both regions are selected
  useEffect(() => {
    if (pickupRegion && destinationRegion) {
      calculatePrice();
    } else {
      setCalculation(null);
    }
  }, [pickupRegion, destinationRegion, selectedService]);

  const loadActiveStartPoint = async () => {
    try {
      setLoading(true);
      const response = await winchStartPointsAPI.getActive();
      setActiveStartPoint(response.data.data.startPoint);
    } catch (error: any) {
      console.error('Failed to load active start point:', error);
      toast.error('تعذر تحميل نقطة البداية النشطة');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = async () => {
    if (!pickupRegion || !destinationRegion) return;

    try {
      setCalculating(true);
      setError(null);
      const response = await winchPricingAPI.calculatePriceByRegions({
        serviceId: selectedService,
        pickupRegionId: pickupRegion.id,
        destinationRegionId: destinationRegion.id,
      });
      setCalculation(response.data.data);
    } catch (error: any) {
      const errorMsg = error.response?.data?.error?.message || 'فشل حساب السعر';
      setError(errorMsg);
      setCalculation(null);
      toast.error(errorMsg);
    } finally {
      setCalculating(false);
    }
  };

  const handleClear = () => {
    setPickupRegion(null);
    setDestinationRegion(null);
    setCalculation(null);
    setError(null);
  };

  const serviceName = selectedService === WINCH_SERVICES.REGULAR ? 'ونش عادي' : 'ونش كامل';

  return (
    <div className="max-w-6xl mx-auto p-6" dir="rtl">
      <div className="card">
        <h2 className="text-2xl font-bold mb-6 text-white">حاسبة أسعار خدمة الونش (حسب المناطق)</h2>

        {/* Active Start Point Info */}
        {activeStartPoint && (
          <div className="mb-6 p-4 bg-primary/10 border border-primary/30 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/20 rounded-lg">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-gray-400">نقطة بداية الونش</p>
                <p className="font-semibold text-white">{activeStartPoint.name}</p>
                {activeStartPoint.address && (
                  <p className="text-xs text-gray-400">{activeStartPoint.address}</p>
                )}
              </div>
              <div className="mr-auto">
                <p className="text-xs text-gray-400">الإحداثيات</p>
                <p className="text-xs font-mono text-primary" dir="ltr">
                  {activeStartPoint.latitude.toFixed(6)}, {activeStartPoint.longitude.toFixed(6)}
                </p>
              </div>
            </div>
          </div>
        )}

        {!activeStartPoint && !loading && (
          <div className="mb-6 p-4 bg-warning/10 border border-warning/30 rounded-lg">
            <p className="text-warning">⚠️ لم يتم تكوين نقطة بداية نشطة. يرجى الاتصال بالمسؤول.</p>
          </div>
        )}

        {/* Service Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            اختر خدمة الونش
          </label>
          <div className="flex gap-4">
            <button
              onClick={() => setSelectedService(WINCH_SERVICES.REGULAR)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                selectedService === WINCH_SERVICES.REGULAR
                  ? 'bg-primary text-dark shadow-glow'
                  : 'bg-dark-lighter text-gray-300 border border-gray-700 hover:border-primary'
              }`}
            >
              🚗 ونش عادي
            </button>
            <button
              onClick={() => setSelectedService(WINCH_SERVICES.FULL_DOWN)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                selectedService === WINCH_SERVICES.FULL_DOWN
                  ? 'bg-primary text-dark shadow-glow'
                  : 'bg-dark-lighter text-gray-300 border border-gray-700 hover:border-primary'
              }`}
            >
              🚙 ونش كامل
            </button>
          </div>
        </div>

        {/* Region Selectors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Pickup Region */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              منطقة الاستلام (Pickup)
            </label>
            <RegionSelector
              value={pickupRegion?.id || null}
              onChange={setPickupRegion}
              placeholder="اختر منطقة الاستلام..."
              showGovernorate={true}
            />
          </div>

          {/* Destination Region */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              منطقة الوجهة (Destination)
            </label>
            <RegionSelector
              value={destinationRegion?.id || null}
              onChange={setDestinationRegion}
              placeholder="اختر منطقة الوجهة..."
              disabled={!pickupRegion}
              showGovernorate={true}
            />
          </div>
        </div>

        {/* Clear Button */}
        {(pickupRegion || destinationRegion) && (
          <div className="mb-6">
            <button
              onClick={handleClear}
              className="px-6 py-2 bg-dark-lighter text-gray-300 rounded-lg font-semibold border border-gray-700 hover:border-primary transition-all"
            >
              مسح الاختيارات
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-danger/20 border border-danger rounded-lg">
            <p className="text-danger font-medium">⚠️ {error}</p>
          </div>
        )}

        {/* Calculating Indicator */}
        {calculating && (
          <div className="mb-6 p-4 bg-info/20 border border-info/30 rounded-lg">
            <p className="text-info font-medium animate-pulse">⏳ جاري حساب السعر...</p>
          </div>
        )}

        {/* Price Calculation Result */}
        {calculation && (
          <div className="space-y-6">
            {/* Fixed Pricing Display */}
            {calculation.pricingModel === 'region_to_region' && calculation.route && (
              <div className="p-6 bg-gradient-to-br from-success/10 to-success/5 border border-success/30 rounded-lg">
                <h3 className="text-lg font-bold text-success mb-4 flex items-center gap-2">
                  <Navigation className="h-5 w-5" />
                  السعر الثابت (حسب المنطقة)
                </h3>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex flex-col items-center gap-1">
                    <div className="p-3 bg-success/20 rounded-full">
                      <MapPin className="h-6 w-6 text-success" />
                    </div>
                    <p className="font-semibold text-white">{calculation.route.fromRegion}</p>
                    <p className="text-xs text-gray-400">منطقة الاستلام</p>
                  </div>

                  <div className="flex-1 flex items-center justify-center px-4">
                    <div className="flex flex-col items-center">
                      <ArrowLeft className="h-6 w-6 text-success mb-1" />
                      <p className="text-sm font-medium text-success">سعر ثابت</p>
                    </div>
                  </div>

                  <div className="flex flex-col items-center gap-1">
                    <div className="p-3 bg-info/20 rounded-full">
                      <MapPin className="h-6 w-6 text-info" />
                    </div>
                    <p className="font-semibold text-white">{calculation.route.toRegion}</p>
                    <p className="text-xs text-gray-400">منطقة الوجهة</p>
                  </div>
                </div>
              </div>
            )}

            {/* Distance-Based Pricing Display */}
            {calculation.pricingModel === 'distance_based' && calculation.startPoint && calculation.distanceBreakdown && (
              <>
                {/* Interactive Map */}
                {calculation.calculationLog && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Map className="h-5 w-5 text-primary" />
                      <h3 className="text-lg font-bold text-white">خريطة المسار التفاعلية</h3>
                    </div>
                    <RouteMap
                      startPoint={{
                        name: calculation.startPoint?.name || calculation.startPoint?.nameAr || calculation.startPoint?.nameEn || 'نقطة البداية',
                        coordinates: calculation.calculationLog.coordinates.startPoint,
                      }}
                      pickup={{
                        name: calculation.pickupRegion?.nameAr || calculation.pickupRegion?.nameEn || 'منطقة الاستلام',
                        coordinates: calculation.calculationLog.coordinates.pickup,
                      }}
                      destination={{
                        name: calculation.destinationRegion?.nameAr || calculation.destinationRegion?.nameEn || 'منطقة الوجهة',
                        coordinates: calculation.calculationLog.coordinates.destination,
                      }}
                      height="450px"
                    />
                  </div>
                )}

                {/* Visual Route */}
                <div className="p-6 bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/30 rounded-lg">
                  <h3 className="text-lg font-bold text-primary mb-4 flex items-center gap-2">
                    <Navigation className="h-5 w-5" />
                    المسار المحسوب
                  </h3>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex flex-col items-center gap-1">
                      <div className="p-3 bg-primary/20 rounded-full">
                        <MapPin className="h-6 w-6 text-primary" />
                      </div>
                      <p className="font-semibold text-white">{calculation.startPoint?.name || calculation.startPoint?.nameAr || calculation.startPoint?.nameEn || 'نقطة البداية'}</p>
                      <p className="text-xs text-gray-400">نقطة البداية</p>
                    </div>

                    <div className="flex-1 flex items-center justify-center px-4">
                      <div className="flex flex-col items-center">
                        <ArrowLeft className="h-6 w-6 text-primary mb-1" />
                        <p className="text-lg font-bold text-primary" dir="ltr">
                          {calculation.distanceBreakdown.startToPickup.toFixed(2)} km
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col items-center gap-1">
                      <div className="p-3 bg-success/20 rounded-full">
                        <MapPin className="h-6 w-6 text-success" />
                      </div>
                      <p className="font-semibold text-white">{calculation.pickupRegion?.nameEn}</p>
                      <p className="text-xs text-gray-400">{calculation.pickupRegion?.nameAr}</p>
                    </div>

                    <div className="flex-1 flex items-center justify-center px-4">
                      <div className="flex flex-col items-center">
                        <ArrowLeft className="h-6 w-6 text-success mb-1" />
                        <p className="text-lg font-bold text-success" dir="ltr">
                          {calculation.distanceBreakdown.pickupToDestination.toFixed(2)} km
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col items-center gap-1">
                      <div className="p-3 bg-info/20 rounded-full">
                        <MapPin className="h-6 w-6 text-info" />
                      </div>
                      <p className="font-semibold text-white">{calculation.destinationRegion?.nameEn}</p>
                      <p className="text-xs text-gray-400">{calculation.destinationRegion?.nameAr}</p>
                    </div>
                  </div>
                </div>

                {/* Distance Breakdown */}
                <div className="p-6 bg-dark-lighter border border-gray-700 rounded-lg">
                  <h3 className="text-lg font-bold text-white mb-4">📏 تفصيل المسافات</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-dark rounded-lg">
                      <span className="text-gray-300">
                        المسافة من نقطة البداية إلى منطقة الاستلام
                      </span>
                      <span className="text-lg font-bold text-primary" dir="ltr">
                        {calculation.distanceBreakdown.startToPickup.toFixed(2)} km
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-dark rounded-lg">
                      <span className="text-gray-300">
                        المسافة من منطقة الاستلام إلى الوجهة
                      </span>
                      <span className="text-lg font-bold text-success" dir="ltr">
                        {calculation.distanceBreakdown.pickupToDestination.toFixed(2)} km
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-primary/10 border border-primary/30 rounded-lg">
                      <span className="text-lg font-semibold text-white">
                        إجمالي المسافة
                      </span>
                      <span className="text-2xl font-bold text-primary" dir="ltr">
                        {calculation.distanceBreakdown.total.toFixed(2)} km
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Calculation Logs */}
            {calculation.calculationLog && (
              <CalculationLogs log={calculation.calculationLog} />
            )}

            {/* Price Result */}
            <div className="p-6 bg-gradient-to-br from-success/20 to-success/10 border border-success/30 rounded-lg">
              <h3 className="text-xl font-bold text-success mb-4">💰 السعر النهائي</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-gray-400 font-medium mb-1">نوع الخدمة</p>
                  <p className="text-lg font-semibold text-white">{serviceName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400 font-medium mb-1">
                    {calculation.pricingModel === 'region_to_region' ? 'نوع التسعير' : 'فئة السعر'}
                  </p>
                  <p className="text-lg font-semibold text-white" dir="ltr">
                    {calculation.pricingModel === 'region_to_region' 
                      ? 'سعر ثابت' 
                      : calculation.tier?.distanceRange || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400 font-medium mb-1">السعر الإجمالي</p>
                  <p className="text-3xl font-bold text-primary" dir="ltr">
                    {calculation.pricing.formattedPrice}
                  </p>
                </div>
              </div>
              
              {/* Additional Info for Fixed Pricing */}
              {calculation.pricingModel === 'region_to_region' && calculation.route && (
                <div className="mt-4 pt-4 border-t border-success/20">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {calculation.route.distanceKm && (
                      <div>
                        <p className="text-gray-400">المسافة التقريبية</p>
                        <p className="text-white font-semibold" dir="ltr">{calculation.route.distanceKm.toFixed(2)} km</p>
                      </div>
                    )}
                    {calculation.route.estimatedTimeMinutes && (
                      <div>
                        <p className="text-gray-400">الوقت المقدر</p>
                        <p className="text-white font-semibold">{calculation.route.estimatedTimeMinutes} دقيقة</p>
                      </div>
                    )}
                  </div>
                  {calculation.route.notes && (
                    <div className="mt-3">
                      <p className="text-gray-400 text-xs">ملاحظات</p>
                      <p className="text-white text-sm">{calculation.route.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Usage Guide */}
      <div className="mt-8 bg-dark-lighter rounded-lg border border-gray-700 p-6">
        <h3 className="text-lg font-bold mb-4 text-white">💡 كيفية الاستخدام</h3>
        <div className="space-y-2 text-sm text-gray-300">
          <p><strong className="text-primary">1. اختر الخدمة:</strong> حدد نوع الونش (عادي أو كامل)</p>
          <p><strong className="text-primary">2. منطقة الاستلام:</strong> اختر المنطقة التي سيتم استلام السيارة منها</p>
          <p><strong className="text-primary">3. منطقة الوجهة:</strong> اختر المنطقة التي سيتم توصيل السيارة إليها</p>
          <p><strong className="text-primary">4. عرض النتيجة:</strong> سيتم حساب المسافة الكاملة والسعر تلقائياً</p>
          <p className="pt-2 border-t border-gray-700 mt-3">
            <strong className="text-success">ملاحظة:</strong> يتم حساب السعر بناءً على إجمالي المسافة من نقطة بداية الونش ({activeStartPoint?.name || 'غير محدد'}) 
            إلى منطقة الاستلام ثم إلى منطقة الوجهة
          </p>
        </div>
      </div>
    </div>
  );
}
